import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Card, SectionTitle, Field, FormError, SubmitButton, LoadingState, ErrorState, StatusBadge } from "@/components/common";
import { fmtTime, fmtDayLong } from "@/modules/scheduling/shared";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { DAYS, fmtYmd } from "@/lib/format";
import { t } from "@/i18n";

const RULES = ["online_capacity_pct", "max_online_per_day", "hold_minutes", "pending_expiry_hours", "lead_time_hours", "horizon_days", "min_notice_hours"];

async function run(fn, done) {
  try {
    await fn();
    toast.success(t("common.saved"));
    done?.();
  } catch (e) {
    toast.error(errMsg(e));
  }
}

function RulesForm({ initial, canManage }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await run(() => api.put("/booking/settings", { ...Object.fromEntries(RULES.map((k) => [k, Number(f[k])])), online_enabled: f.online_enabled, slot_step_min: Number(f.slot_step_min) }));
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="booking-rules-form">
      <label className="flex items-center gap-3 text-sm font-medium"><Switch checked={f.online_enabled} onCheckedChange={(v) => setF({ ...f, online_enabled: v })} data-testid="rule-online-enabled" /> {t("avail.onlineEnabled")}</label>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {RULES.map((k) => <Field key={k} label={t(`avail.rules.${k}`)} hint={t(`avail.hints.${k}`)}><Input type="number" min={0} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`rule-${k}`} /></Field>)}
        <Field label={t("avail.rules.slot_step_min")}>
          <Select value={String(f.slot_step_min)} onValueChange={(v) => setF({ ...f, slot_step_min: Number(v) })}>
            <SelectTrigger data-testid="rule-slot-step"><SelectValue /></SelectTrigger>
            <SelectContent>{[15, 30, 60].map((n) => <SelectItem key={n} value={String(n)}>{n} min</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      <SubmitButton busy={busy} disabled={!canManage} data-testid="booking-rules-save">{t("common.save")}</SubmitButton>
    </form>
  );
}

const weeklyHours = (g) => Object.fromEntries(DAYS.map((d) => {
  const h = (g.hours || {})[d] || { open: false };
  return [d, { open: !!h.open, start: h.start || "08:00", end: h.end || "17:00", breaks: [] }];
}));

function PeriodHours({ i, hours, onChange, disabled }) {
  const setDay = (d, patch) => onChange({ ...hours, [d]: { ...hours[d], ...patch } });
  return (
    <div className="w-full divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white" data-testid={`period-hours-${i}`}>
      {DAYS.map((d) => {
        const day = hours[d];
        const br = day.breaks?.[0];
        return (
          <div key={d} className="flex flex-wrap items-center gap-2 p-2 text-sm">
            <span className="w-24 font-medium">{t(`days.${d}`)}</span>
            <Switch checked={day.open} disabled={disabled} onCheckedChange={(v) => setDay(d, { open: v })} data-testid={`period-${i}-${d}-open`} />
            {day.open && <>
              <Input type="time" disabled={disabled} value={day.start} onChange={(e) => setDay(d, { start: e.target.value })} className="w-28" data-testid={`period-${i}-${d}-start`} />
              <span>–</span>
              <Input type="time" disabled={disabled} value={day.end} onChange={(e) => setDay(d, { end: e.target.value })} className="w-28" data-testid={`period-${i}-${d}-end`} />
              <span className="ml-2 text-xs text-slate-500">{t("b10.periods.breakLabel")}</span>
              <Input type="time" disabled={disabled} value={br?.start || ""} onChange={(e) => setDay(d, { breaks: e.target.value ? [{ start: e.target.value, end: br?.end || e.target.value }] : [] })} className="w-28" data-testid={`period-${i}-${d}-break-start`} />
              <Input type="time" disabled={disabled} value={br?.end || ""} onChange={(e) => setDay(d, { breaks: br ? [{ ...br, end: e.target.value }] : [] })} className="w-28" data-testid={`period-${i}-${d}-break-end`} />
            </>}
          </div>
        );
      })}
      {!disabled && <div className="p-2"><Button type="button" variant="ghost" size="sm" onClick={() => onChange(Object.fromEntries(DAYS.map((d) => [d, { ...hours.mon, breaks: [...(hours.mon.breaks || [])] }])))} data-testid={`period-${i}-copy-monday`}>{t("b10.periods.copy")}</Button></div>}
    </div>
  );
}

function PeriodsForm({ initial, canManage }) {
  const { garage } = useGarage();
  const [rows, setRows] = useState(initial || []);
  const [busy, setBusy] = useState(false);
  const set = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const save = async () => {
    setBusy(true);
    try {
      const clean = (h) => h && Object.fromEntries(DAYS.map((d) => [d, { ...h[d], breaks: (h[d].breaks || []).filter((b) => b.start && b.end && b.start < b.end) }]));
      const { data } = await api.put("/booking/periods", { periods: rows.filter((r) => r.start && r.end).map((r) => ({ ...r, hours: clean(r.hours) })) });
      setRows(data.periods);
      toast.success(t("b9.periods.saved"));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4" data-testid="booking-periods-form">
      {rows.length === 0 && <p className="text-sm text-slate-500" data-testid="booking-periods-empty">{t("b9.periods.none")}</p>}
      {rows.map((r, i) => (
        <div key={i} className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 p-3" data-testid={`booking-period-${i}`}>
          <Field label={t("b9.periods.start")}><Input type="date" value={r.start} disabled={!canManage} onChange={(e) => set(i, { start: e.target.value })} className="w-44" data-testid={`period-start-${i}`} /></Field>
          <Field label={t("b9.periods.end")}><Input type="date" value={r.end} min={r.start} disabled={!canManage} onChange={(e) => set(i, { end: e.target.value })} className="w-44" data-testid={`period-end-${i}`} /></Field>
          <Field label={t("b9.periods.label")}><Input value={r.label || ""} disabled={!canManage} onChange={(e) => set(i, { label: e.target.value })} className="w-56" data-testid={`period-label-${i}`} /></Field>
          {canManage && <Button variant="ghost" size="icon" className="text-red-600" onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label={t("common.remove")} data-testid={`period-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>}
          <div className="w-full space-y-2">
            <p className="text-xs font-semibold text-slate-700">{t("b10.periods.hoursTitle")}</p>
            {r.hours ? <PeriodHours i={i} hours={r.hours} disabled={!canManage} onChange={(h) => set(i, { hours: h })} />
              : <p className="text-xs text-slate-500">{t("b10.periods.legacy")} {canManage && <Button type="button" variant="link" size="sm" onClick={() => set(i, { hours: weeklyHours(garage) })} data-testid={`period-${i}-custom-hours`}>{t("b10.periods.custom")}</Button>}</p>}
          </div>
        </div>
      ))}
      {canManage && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setRows([...rows, { start: "", end: "", label: "", hours: weeklyHours(garage) }])} data-testid="period-add"><Plus className="mr-1 h-4 w-4" /> {t("b9.periods.add")}</Button>
          <SubmitButton type="button" busy={busy} onClick={save} data-testid="periods-save">{t("common.save")}</SubmitButton>
        </div>
      )}
    </div>
  );
}

function StaffCard({ m, canManage, onSaved }) {
  const [f, setF] = useState({ bookable: m.bookable, skills: (m.skills || []).join(", "), schedule: m.schedule });
  const setDay = (d, patch) => setF({ ...f, schedule: { ...f.schedule, [d]: { ...f.schedule[d], ...patch } } });
  const save = () => run(() => api.put(`/booking/staff/${m.membership_id}`, { bookable: f.bookable, skills: f.skills.split(","), schedule: f.schedule }), onSaved);
  return (
    <Card testId={`staff-hours-${m.name.split(" ")[0].toLowerCase()}`}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div><p className="font-display text-lg font-bold">{m.name}</p><StatusBadge status={m.role} /></div>
        <label className="flex items-center gap-2 text-sm"><Switch checked={f.bookable} onCheckedChange={(v) => setF({ ...f, bookable: v })} data-testid="staff-bookable-switch" /> {t("avail.bookable")}</label>
      </div>
      <Field label={t("avail.skills")} hint={t("avail.skillsHint")}><Input value={f.skills} onChange={(e) => setF({ ...f, skills: e.target.value })} data-testid="staff-skills-input" /></Field>
      <div className="mt-4 divide-y divide-slate-100 rounded-lg border border-slate-200">
        {DAYS.map((d) => {
          const day = f.schedule[d] || { open: false, start: "08:00", end: "17:00", breaks: [] };
          const br = day.breaks?.[0];
          return (
            <div key={d} className="flex flex-wrap items-center gap-2 p-2 text-sm">
              <span className="w-24 font-medium">{t(`days.${d}`)}</span>
              <Switch checked={day.open} onCheckedChange={(v) => setDay(d, { open: v })} data-testid={`staff-${d}-open`} />
              {day.open && <>
                <Input type="time" value={day.start} onChange={(e) => setDay(d, { start: e.target.value })} className="w-28" />
                <span>–</span>
                <Input type="time" value={day.end} onChange={(e) => setDay(d, { end: e.target.value })} className="w-28" />
                <span className="ml-2 text-xs text-slate-500">{t("avail.break")}</span>
                <Input type="time" value={br?.start || ""} onChange={(e) => setDay(d, { breaks: e.target.value ? [{ start: e.target.value, end: br?.end || e.target.value }] : [] })} className="w-28" data-testid={`staff-${d}-break-start`} />
                <Input type="time" value={br?.end || ""} onChange={(e) => setDay(d, { breaks: br ? [{ ...br, end: e.target.value }] : [] })} className="w-28" data-testid={`staff-${d}-break-end`} />
              </>}
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-slate-500">{t("avail.staffHint")}</p>
      {canManage && <Button className="mt-4" onClick={save} data-testid="staff-hours-save">{t("common.save")}</Button>}
    </Card>
  );
}

function ListForm({ path, fields, staff, render, canCreate, testId }) {
  const { data, error, loading, reload } = useFetch(path);
  const [f, setF] = useState(Object.fromEntries(fields.map((x) => [x.k, x.def || ""])));
  const [err, setErr] = useState("");
  const add = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post(path, { ...f, user_id: f.user_id === "__all" ? null : f.user_id, ...(f.count ? { count: Number(f.count) } : {}) });
      toast.success(t("common.saved"));
      reload();
    } catch (x) {
      setErr(errMsg(x));
    }
  };
  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div className="space-y-5" data-testid={testId}>
      {canCreate && (
        <form onSubmit={add} className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 p-4">
          <FormError message={err} />
          {fields.map((x) => (
            <Field key={x.k} label={t(`avail.f.${x.k}`)}>
              {x.type === "staff" ? (
                <Select value={f[x.k]} onValueChange={(v) => setF({ ...f, [x.k]: v })}>
                  <SelectTrigger className="w-44" data-testid={`${testId}-${x.k}`}><SelectValue placeholder={t("common.select")} /></SelectTrigger>
                  <SelectContent>{x.all && <SelectItem value="__all">{t("avail.allStaff")}</SelectItem>}{staff.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              ) : x.type === "kind" ? (
                <Select value={f[x.k]} onValueChange={(v) => setF({ ...f, [x.k]: v })}>
                  <SelectTrigger className="w-36" data-testid={`${testId}-${x.k}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{["internal", "emergency"].map((k) => <SelectItem key={k} value={k}>{t(`status.${k}`)}</SelectItem>)}</SelectContent>
                </Select>
              ) : <Input type={x.type || "text"} required={x.req} value={f[x.k]} onChange={(e) => setF({ ...f, [x.k]: e.target.value })} className={x.type === "text" || !x.type ? "w-56" : "w-40"} data-testid={`${testId}-${x.k}`} />}
            </Field>
          ))}
          <Button type="submit" data-testid={`${testId}-add`}><Plus className="mr-1 h-4 w-4" /> {t("avail.add")}</Button>
        </form>
      )}
      {data.length === 0 ? <p className="text-sm text-slate-500">{t("avail.none")}</p> : data.map((row) => (
        <div key={row.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3 text-sm">
          <span>{render(row)}</span>
          {canCreate && <Button variant="ghost" size="icon" className="text-red-600" onClick={() => run(() => api.delete(`${path}/${row.id}`), reload)} data-testid={`${testId}-remove-${row.id}`} aria-label={t("common.remove")}><Trash2 className="h-4 w-4" /></Button>}
        </div>
      ))}
    </div>
  );
}

export default function Availability() {
  const { garage, canManage, readOnly } = useGarage();
  const tz = garage.timezone;
  const { data, error, loading, reload } = useFetch("/booking/settings");
  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const name = (uid) => data.staff.find((m) => m.user_id === uid)?.name || t("avail.allStaff");
  return (
    <div>
      <PageHeader title={t("avail.title")} subtitle={t("avail.subtitle")} actions={<Button asChild variant="outline" data-testid="availability-manage-services"><Link to="/pro/settings?tab=services">{t("services.manage")}</Link></Button>} />
      <Tabs defaultValue="rules">
        <TabsList className="mb-6 flex h-auto flex-wrap justify-start bg-white p-1 shadow-sm">
          {["rules", "periods", "staff", "absences", "blocks", "resources"].map((k) => <TabsTrigger key={k} value={k} data-testid={`avail-tab-${k}`} className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">{k === "periods" ? t("b9.periods.tab") : t(`avail.tabs.${k}`)}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="rules"><Card><SectionTitle hint={t("avail.rulesHint")}>{t("avail.tabs.rules")}</SectionTitle><RulesForm initial={data.settings} canManage={canManage} /></Card></TabsContent>
        <TabsContent value="periods"><Card><SectionTitle hint={t("b10.periods.hint")}>{t("b9.periods.title")}</SectionTitle><PeriodsForm initial={data.settings.periods} canManage={canManage} /></Card></TabsContent>
        <TabsContent value="staff"><div className="grid gap-6 xl:grid-cols-2">{data.staff.map((m) => <StaffCard key={m.membership_id} m={m} canManage={canManage} onSaved={reload} />)}</div></TabsContent>
        <TabsContent value="absences"><Card><SectionTitle hint={t("avail.absencesHint")}>{t("avail.tabs.absences")}</SectionTitle>
          <ListForm path="/booking/absences" testId="absences" staff={data.staff} canCreate={canManage}
            fields={[{ k: "user_id", type: "staff" }, { k: "date_from", type: "date", req: true }, { k: "date_to", type: "date", req: true }, { k: "reason" }]}
            render={(a) => <><strong>{name(a.user_id)}</strong> · {fmtYmd(a.date_from)} → {fmtYmd(a.date_to)} {a.reason && `· ${a.reason}`}</>} /></Card></TabsContent>
        <TabsContent value="blocks"><Card><SectionTitle hint={t("avail.blocksHint")}>{t("avail.tabs.blocks")}</SectionTitle>
          <ListForm path="/booking/blocks" testId="blocks" staff={data.staff} canCreate={!readOnly}
            fields={[{ k: "user_id", type: "staff", all: true, def: "__all" }, { k: "date", type: "date", req: true }, { k: "start", type: "time", req: true }, { k: "end", type: "time", req: true }, { k: "kind", type: "kind", def: "internal" }, { k: "reason", req: true }]}
            render={(b) => <><StatusBadge status={b.kind} /> <strong>{name(b.user_id)}</strong> · {fmtDayLong(b.start, tz)} {fmtTime(b.start, tz)}–{fmtTime(b.end, tz)} · {b.reason}</>} /></Card></TabsContent>
        <TabsContent value="resources"><Card><SectionTitle hint={t("avail.resourcesHint")}>{t("avail.tabs.resources")}</SectionTitle>
          <ListForm path="/booking/resources" testId="resources" staff={data.staff} canCreate={canManage}
            fields={[{ k: "name", req: true }, { k: "count", type: "number", req: true, def: "1" }]}
            render={(r) => <><strong>{r.name}</strong> · ×{r.count}</>} /></Card></TabsContent>
      </Tabs>
    </div>
  );
}
